import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon, PencilIcon, CheckIcon, XIcon } from '@heroicons/react/outline';

const SettingsCard = ({ title, options, onUpdate, settingKey }) => {
  const [items, setItems] = useState(options);
  const [newItem, setNewItem] = useState('');
  const [editingItem, setEditingItem] = useState(null);

  const handleAddItem = () => {
    if (newItem && !items.includes(newItem)) {
      const updatedItems = [...items, newItem];
      setItems(updatedItems);
      onUpdate(settingKey, updatedItems);
      setNewItem('');
    }
  };

  const handleRemoveItem = (itemToRemove) => {
    const updatedItems = items.filter(item => item !== itemToRemove);
    setItems(updatedItems);
    onUpdate(settingKey, updatedItems);
  };

  const handleStartEditing = (index) => {
    setEditingItem({ index, value: items[index] });
  };

  const handleCancelEditing = () => {
    setEditingItem(null);
  };

  const handleSaveEditing = () => {
    if (editingItem === null || !editingItem.value) return;

    const otherItems = items.filter((_, i) => i !== editingItem.index);
    if (otherItems.includes(editingItem.value)) {
        alert("Cette valeur existe déjà dans la liste.");
        return;
    }

    const updatedItems = [...items];
    updatedItems[editingItem.index] = editingItem.value;
    setItems(updatedItems);
    onUpdate(settingKey, updatedItems);
    setEditingItem(null);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      <ul className="space-y-2 mb-4">
        {items.map((item, index) => (
          <li key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
            {editingItem && editingItem.index === index ? (
              <>
                <input
                  type="text"
                  value={editingItem.value}
                  onChange={(e) => setEditingItem({ ...editingItem, value: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveEditing()}
                  className="flex-grow p-1 border border-blue-500 rounded-md"
                  autoFocus
                />
                <div className="flex items-center ml-2 space-x-2">
                  <button onClick={handleSaveEditing} className="text-green-600 hover:text-green-800">
                    <CheckIcon className="h-5 w-5" />
                  </button>
                  <button onClick={handleCancelEditing} className="text-red-600 hover:text-red-800">
                    <XIcon className="h-5 w-5" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <span>{item}</span>
                <div className="flex items-center ml-2 space-x-2">
                  <button onClick={() => handleStartEditing(index)} className="text-blue-600 hover:text-blue-800">
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button onClick={() => handleRemoveItem(item)} className="text-gray-500 hover:text-red-700">
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
      <div className="flex space-x-2">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAddItem()}
          className="flex-grow p-2 border border-gray-300 rounded-md"
          placeholder="Nouvelle option"
        />
        <button onClick={handleAddItem} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center">
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://venteclead.st-info.fr/get_settings.php');
      if (!response.ok) throw new Error(`Erreur serveur: ${response.status}`);
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      setSettings(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des paramètres:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleUpdateSetting = async (key, value) => {
    try {
      const response = await fetch('https://venteclead.st-info.fr/update_setting.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value })
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Réponse invalide' }));
        throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
      }
      const result = await response.json();
      if (result.error) throw new Error(result.error);
      
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch (error) {
      console.error("Erreur lors de la mise à jour du paramètre:", error);
      alert(`La mise à jour a échoué: ${error.message}`);
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Chargement des paramètres...</div>;
  }

  const settingsConfig = [
    { key: 'prospect_statut', title: 'Statuts des Prospects' },
    { key: 'prospect_etat', title: 'États des Prospects' },
    { key: 'user_roles', title: 'Rôles des Utilisateurs' },
    { key: 'predefined_comments', title: 'Commentaires Prédéfinis (Historique)' }
  ];

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Paramètres de l'Application</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {settingsConfig.map(config => (
          settings[config.key] && (
            <SettingsCard
              key={config.key}
              title={config.title}
              options={settings[config.key]}
              onUpdate={handleUpdateSetting}
              settingKey={config.key}
            />
          )
        ))}
      </div>
    </div>
  );
};

export default SettingsPage; 